import { Location } from '@angular/common';
import { Injectable, signal, WritableSignal } from '@angular/core'
import { ApiService } from './api.service'
import { NaturalStoneColor } from '../interfaces/natural-stone/natural-stone-color'
import {
    NaturalStoneLot,
    NaturalStoneLots
} from '../interfaces/natural-stone/natural-stone-lots'
import { firstValueFrom } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { NaturalStoneCartLine } from '../interfaces/natural-stone/natural-stone-cart'

@Injectable({
    providedIn: 'root',
})
export class NaturalStoneService {

    isUsa: boolean = false;

    fullColorList: WritableSignal<NaturalStoneColor[]> = signal([])
    filteredColorList: WritableSignal<NaturalStoneColor[]> = signal([])
    selectedColor: WritableSignal<NaturalStoneLots> = signal({})

    centerOptions: WritableSignal<{centerId: string, centerName: string}[]> = signal([])
    selectedCenter: WritableSignal<string> = signal('')

    brandOptions: WritableSignal<string[]> = signal([])
    selectedBrand: WritableSignal<string> = signal('')

    thicknessOptions: WritableSignal<string[]> = signal([])
    selectedThickness: WritableSignal<string> = signal('')

    finishOptions: WritableSignal<string[]> = signal([])
    selectedFinish: WritableSignal<string> = signal('')

    priceGroupOptions: WritableSignal<string[]> = signal([])
    selectedPriceGroup: WritableSignal<string> = signal('')

    searchString: WritableSignal<string> = signal('')

    orderByOptions: WritableSignal<OrderBy[]> = signal(['launch_date', 'alphabetical'])
    orderBy: WritableSignal<OrderBy> = signal('launch_date')

    cart: WritableSignal<NaturalStoneCartLine[]> = signal([])
    isMinicartOpen: WritableSignal<boolean> = signal(false)

    constructor(private api: ApiService, private location: Location, private translate: TranslateService) {
        this.isUsa = this.api.isUsaUser()
    }

    fetchColorList(forceUpdate = false){
        if(!forceUpdate && this.fullColorList().length > 0) return

        const centerId = this.api.userInfo.getValue().sapCenterId
        const centerName = this.api.userInfo.getValue().centerName

        const centers: {centerId: string, centerName: string}[] = []
        if(centerId && centerName && centerId !== '1070'){
            centers.push({centerId, centerName})
        }
        centers.push({centerId: '1070', centerName: 'Cosentino HQ'})

        if(!this.selectedCenter()){
            this.selectedCenter.set(centers[0].centerId)
        }
        this.centerOptions.set(centers)

        this.api.getNaturalStoneColorList(this.selectedCenter()).subscribe({
            next: (data) => {
                this.fullColorList.set(data.map(value => {
                    value.dateLaunchParsed = new Date(value.dateLaunch!)
                    value.completeData = true
                    return value
                }))

                this.setupFilters(data)

                this.filterData()
            },
            error: (err) => {
                console.log(err)
            },
            complete: () => {

            }
        })
    }

    private setupFilters(data: NaturalStoneColor[]){
        const brands = new Set<string>()
        const thicknesses = new Set<string>()
        const finishes = new Set<string>()
        const priceGroups = new Set<string>()
        for(let color of data){
            if(color.brand) brands.add(color.brand)
            if(color.thicknesses) color.thicknesses.forEach(thickness => {if(thickness) thicknesses.add(thickness)})
            if(color.finishes) color.finishes.forEach(finish => {if(finish) finishes.add(finish)})
            if(color.priceGroup) priceGroups.add(color.priceGroup)
        }

        this.brandOptions.set([...brands].sort(this.brandsComparator))
        if(!this.brandOptions().includes(this.selectedBrand())) this.selectedBrand.set('')

        this.thicknessOptions.set([...thicknesses].sort())
        if(!this.thicknessOptions().includes(this.selectedThickness())) this.selectedThickness.set('')

        this.finishOptions.set([...finishes].sort())
        if(!this.finishOptions().includes(this.selectedFinish())) this.selectedFinish.set('')

        this.priceGroupOptions.set([...priceGroups].sort())
        if(!this.priceGroupOptions().includes(this.selectedPriceGroup())) this.selectedPriceGroup.set('')
    }

    private filterData(){
        this.filteredColorList.set(
                this.fullColorList().filter(color => {
                    const brandMatch = this.selectedBrand() === '' || color.brand?.toLowerCase() === this.selectedBrand().toLowerCase()
                    const thicknessMatch = this.selectedThickness() === '' || color.thicknesses?.includes(this.selectedThickness())
                    const finishMatch = this.selectedFinish() === '' || color.finishes?.includes(this.selectedFinish())
                    const priceGroupMatch = this.selectedPriceGroup() === '' || color.priceGroup?.toLowerCase() === this.selectedPriceGroup().toLowerCase()
                    const searchString = !this.searchString() || color.colorName?.toLowerCase().includes(this.searchString().toLowerCase()) || color.colorCode?.toLowerCase().includes(this.searchString().toLowerCase())
                    return brandMatch && thicknessMatch && finishMatch && priceGroupMatch && searchString
            }).sort(this.sortFilteredResults.bind(this))
        )
    }

    private sortData(){
        this.filteredColorList.update((prevList) => prevList.sort(this.sortFilteredResults.bind(this))) //NOSONAR
    }

    private sortFilteredResults(a: NaturalStoneColor, b: NaturalStoneColor): number {
        switch(this.orderBy()){
            case 'alphabetical':
                return this.alphabeticalSort(a,b)
            case 'launch_date':
            default:
                // If they have the same launch date (compareValue = 0) order in alphabetical order
                const compareValue = this.launchDateSort(a,b)
                return compareValue ? compareValue : this.alphabeticalSort(a,b)
        }
    }

    private alphabeticalSort(a: NaturalStoneColor, b: NaturalStoneColor): number {
        const aColorName = a.colorName ? a.colorName.toLowerCase() : a.colorCode!
        const bColorName = b.colorName ? b.colorName.toLowerCase() : b.colorCode!
        return aColorName.localeCompare(bColorName)
    }

    private launchDateSort(a: NaturalStoneColor, b: NaturalStoneColor): number {
        if(!a.dateLaunchParsed && !b.dateLaunchParsed){
            return 0;
        }

        if(!a.dateLaunchParsed && b.dateLaunchParsed){
            return 1;
        }

        if(a.dateLaunchParsed && !b.dateLaunchParsed){
            return -1;
        }

        return b.dateLaunchParsed!.valueOf() - a.dateLaunchParsed!.valueOf()
    }

    getBrandText(color?: NaturalStoneColor){
        if(!color){
            color = this.selectedColor()
        }

        if(color.brand){
            return color.brand[0].toUpperCase() + color.brand.substring(1).toLowerCase()
        }

        return undefined
    }

    getColorText(color?: NaturalStoneColor){
        if(!color){
            color = this.selectedColor()
        }

        const name = color.colorName
        if(!name){
            return color.colorCode
        }
        const capitalizedWords = name.split(' ').map((word) => {
            if(word.length > 1){
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            } else if(word.length === 1){
                return word.toUpperCase()
            } else {
                return word
            }
        })
        return capitalizedWords.join(' ')
    }

    async loadColor(centerId: string, sku: string){
        if(this.selectedColor().skus && this.selectedCenter() === centerId){
            await this.fetchLotsForSelectedColor()
            this.updateUrlWithNewSku()
        } else {
            this.selectedCenter.set(centerId)
            const data = await firstValueFrom(this.api.getNaturalStoneLotList(this.selectedCenter(), sku, true))
            if(data && data.lots){
                data.lots = data.lots.map(this.parseLotNumbers)
                if(this.selectedColor().skus){
                    this.selectedColor.update(prevColor => {
                        const newColor = {...prevColor}
                        newColor.sku = data.sku
                        newColor.lots = data.lots

                        return newColor
                    })
                } else {
                    this.selectedColor.set(data)
                }
            } else {
                console.error("invalid response", data)
            }
        }
    }

    selectColor(color: NaturalStoneColor){
        if(!color.skus){
            console.error("skus is empty", color)
            return;
        }
        const finishThicknessKey = Object.keys(color.skus)[0]
        const finish = finishThicknessKey.split('-')[0]
        const thickness = finishThicknessKey.split('-')[1]
        const selectedColor = {
            ...color,
            sku: color.skus[finishThicknessKey],
            finish: finish,
            thickness: thickness
        }
        this.selectedColor.set(selectedColor)
    }

    getAvailableSlabsForLotAndBundle(lotId: string, bundleId?: string, ignoreCart = false){
        let slabs = 0
        if(bundleId){
            const color = this.selectedColor()
            if(!color.lots) return slabs;
            color.lots.forEach(lot => {
                if(lot.bundleId === bundleId){
                    slabs += 1
                }
            })
            // Remove the bundle slabs that have been added to the cart
            if(!ignoreCart){
                const cartItem = this.cart().find(item => item.lot.bundleId === bundleId)
                if(cartItem){
                    slabs -= cartItem.qty
                }
            }
        } else {
            slabs = 1;

            // Remove it from available stock if in cart
            if(!ignoreCart){
                const cartItem = this.cart().find(item => item.lot.lotId === lotId)
                if(cartItem) slabs = 0;
            }
        }

        return slabs;
    }

    changeCenter(value: string) {
        this.selectedCenter.set(value)
        this.fetchColorList(true)
    }

    changeBrand(value: string) {
        this.selectedBrand.set(value)
        this.filterData()
    }

    changeThickness(value: string) {
        this.selectedThickness.set(value)
        this.filterData()
    }

    changeFinish(value: string) {
        this.selectedFinish.set(value)
        this.filterData()
    }

    changePriceGroup(value: string) {
        this.selectedPriceGroup.set(value)
        this.filterData()
    }

    changeSearchString(value: string) {
        this.searchString.set(value)
        this.filterData()
    }

    changeOrderBy(value: OrderBy){
        this.orderBy.set(value)
        this.sortData()
    }

    changeLotFinish(value: string){
        const newFinish = value

        let newThickness = this.selectedColor().thickness
        const availableThicknesses = this.getAvailableThicknessesForFinish(newFinish);
        if(!newThickness || !availableThicknesses.includes(newThickness)){
            newThickness = availableThicknesses[0]
        }

        this.loadNewColor(newFinish, newThickness)
    }

    changeLotThickness(value: string){
        const newThickness = value

        let newFinish = this.selectedColor().finish
        const availableFinishes = this.getAvailableFinishesForThickness(newThickness);
        if(!newFinish || !availableFinishes.includes(newFinish)){
            newFinish = availableFinishes[0]
        }

        this.loadNewColor(newFinish, newThickness)
    }

    private loadNewColor(newFinish: string, newThickness: string){

        const newSku = this.selectedColor().skus![newFinish + '-' + newThickness]

        this.selectedColor.update(prevValue => {
            const newColor = {...prevValue}
            newColor.sku = newSku
            newColor.finish = newFinish
            newColor.thickness = newThickness

            return newColor
        })

        this.loadColor(this.selectedCenter(), newSku)
    }

    getAvailableThicknessesForFinish(finish: string){
        const color = this.selectedColor()
        if(!color.skus) return <string[]>[]

        const availableThicknesses: Set<string> = new Set<string>()

        if(color.skus){
            const keys = Object.keys(color.skus)
            keys.forEach(key => {
                const splitFinishThickness = key.split('-')
                if(splitFinishThickness[0] === finish){
                    availableThicknesses.add(splitFinishThickness[1])
                }
            })
        }

        return [...availableThicknesses]
    }


    getAvailableFinishesForThickness(thickness: string){
        const color = this.selectedColor()
        if(!color.skus) return <string[]>[]

        const availableFinishes: Set<string> = new Set<string>()

        if(color.skus){
            const keys = Object.keys(color.skus)
            keys.forEach(key => {
                const splitFinishThickness = key.split('-')
                if(splitFinishThickness[1] === thickness){
                    availableFinishes.add(splitFinishThickness[0])
                }
            })
        }

        return [...availableFinishes]
    }

    getTotalInCart(): number{
        let total = 0
        for(let cartLine of this.cart()){
            total += cartLine.qty
        }
        return total
    }

    addLotToCart(lot: NaturalStoneLot, qty: number){
        const currentCart = this.cart()
        const existingIndex = currentCart.findIndex(item => item.lot.lotId === lot.lotId)
        if(existingIndex !== -1){
            currentCart[existingIndex].qty += qty
        } else {
            currentCart.push({
                lot: lot,
                qty: qty,
                maxQty: this.getAvailableSlabsForLotAndBundle(lot.lotId!, lot.bundleId, true),
                color: this.selectedColor()
            })
        }
        this.cart.set(currentCart)
    }

    removeLotFromCart(lot: NaturalStoneLot){
        const currentCart = this.cart()
        const existingIndex = currentCart.findIndex(item => item.lot.lotId === lot.lotId)
        if(existingIndex !== -1){
            currentCart.splice(existingIndex, 1)
        }
        this.cart.set(currentCart)
    }

    clearCart(){
        this.cart.set([])
    }


    private parseLotNumbers(lot: NaturalStoneLot){
        lot.heightEur = parseFloat(<string><unknown>lot.heightEur)
        lot.heightUsa = parseFloat(<string><unknown>lot.heightUsa)
        lot.widthEur = parseFloat(<string><unknown>lot.widthEur)
        lot.widthUsa = parseFloat(<string><unknown>lot.widthUsa)
        lot.surfaceAreaEur = parseFloat(<string><unknown>lot.surfaceAreaEur)
        lot.surfaceAreaUsa = parseFloat(<string><unknown>lot.surfaceAreaUsa)
        return lot;
    }

    private async fetchLotsForSelectedColor(){
        const color = this.selectedColor()
        if(!color.sku){
            console.error("sku is empty", this.selectedColor())
            return;
        }
        const data = await firstValueFrom(this.api.getNaturalStoneLotList(this.selectedCenter(), color.sku, false))
        if(data && data.lots){
            this.selectedColor.update(value => {
                const newColor = {...value}
                newColor.lots = data.lots!.map(this.parseLotNumbers)
                newColor.sku = data.sku!
                return newColor
            })
        } else {
            console.error("invalid fetchLotsForSelectedColor response", data)
        }
    }

    private updateUrlWithNewSku(){
        const currentUrl = this.location.path(false)
        if(currentUrl.includes("/natural-stone/lots/")){
            const splitUrl = currentUrl.split("/")
            const sku = splitUrl[splitUrl.length - 1]
            const newSku = this.selectedColor().sku
            if(newSku && sku !== newSku){
                splitUrl[splitUrl.length - 1] = newSku
                this.location.replaceState(splitUrl.join("/"))
            }
        }
    }

    private brandsComparator(a: string, b: string): number {
        const brandOrder = ['silestone', 'dekton', 'sensa', 'scalea']

        const aLower = a.toLowerCase()
        const bLower = b.toLowerCase()

        const indexA = brandOrder.indexOf(aLower)
        const indexB = brandOrder.indexOf(bLower)

        // If both brands are in the order list, sort by their position
        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB
        }

        // If only 'a' is in the order list, it comes first
        if (indexA !== -1) {
            return -1
        }

        // If only 'b' is in the order list, it comes first
        if (indexB !== -1) {
            return 1
        }

        // If neither is in the order list, sort alphabetically
        return a.localeCompare(b);
    }

}

export type OrderBy = 'launch_date' | 'alphabetical'